import {
  ITrip,
  ITripRoom,
  TripRoomGuestID,
  TripUserPaymentMethod,
  PaymentRequest,
  PaymentTransactionStatusEnum,
  PaymentMethodProviderEnum,
} from "@cruisen/contract"
import { useEffect } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import {
  fetchAllTripUsersProfiles,
  revealPassportNumber,
} from "../../../services/firebase-functions/profile"
import {
  generateProfileGridData,
  generateSkeletonProfile,
  prepareTripRoomGuestProfiles,
} from "./utils"
import { generateFakeProfileGridData } from "../../../utils/demoTripLogic"
import { _t } from "@cruisen/i18n"

export const getRoomDetails = (rooms: ITripRoom[], currentRoomId: string) => {
  const tripRooms = rooms.reduce((acc, room) => {
    if (room.guests?.length > 0) {
      acc.push({
        id: room.id,
        text: room.name,
      })
    }
    return acc
  }, [])
  const roomData = rooms.find((room) => room.id === currentRoomId)
  return { tripRooms, roomData }
}

export const useRoomDetails = ({
  selectedRoomId,
  selectedGuestId,
  setSelectedGuest,
  setSelectedPaymentMethod,
  setFilteredPaymentMethods,
  paymentMethods,
  activeTrip,
  approvedPaymentRequest,
  unmaskedPassportNumber,
  setUnmaskedPassportNumber,
}: {
  selectedRoomId: string
  selectedGuestId: TripRoomGuestID
  setSelectedGuest: (guestId: TripRoomGuestID) => void
  setSelectedPaymentMethod: (paymentMethod: TripUserPaymentMethod) => void
  setFilteredPaymentMethods: (paymentMethods: TripUserPaymentMethod[]) => void
  paymentMethods: TripUserPaymentMethod[]
  activeTrip: ITrip
  approvedPaymentRequest: PaymentRequest | null
  unmaskedPassportNumber: string | null
  setUnmaskedPassportNumber: (unmaskedPassportNumber: string | null) => void
}) => {
  const { tripRooms, roomData } = getRoomDetails(activeTrip.rooms, selectedRoomId)
  const isDemoTrip = activeTrip?.tags?.includes("demo")

  const guests = roomData?.guests ?? []
  const initialGuest = guests[0]

  const { data: profiles, isLoading: isLoadingProfiles } = useQuery({
    queryKey: ["allProfiles", activeTrip.id],
    queryFn: () => fetchAllTripUsersProfiles(activeTrip.id),
    staleTime: 0,
  })

  useEffect(() => {
    if (initialGuest) {
      setSelectedGuest(initialGuest.id)
    }
  }, [initialGuest])

  /** @TODO add check for ACH in the future */
  const filteredPaymentMethods = paymentMethods.filter((paymentMethod) => {
    if (isDemoTrip) return true
    return approvedPaymentRequest?.lineItems[0].transactions.some(
      (transaction) =>
        transaction.userId === paymentMethod.userId &&
        // show only payment method that was approved
        transaction.paymentMethodProvider === PaymentMethodProviderEnum.CreditCard &&
        transaction.creditCardId?.toString() ===
          paymentMethod.tokenizedCreditCard?.id?.toString() &&
        (transaction.status === PaymentTransactionStatusEnum.Approved ||
          transaction.status === PaymentTransactionStatusEnum.Succeeded),
    )
  })
  const initialPaymentMethod =
    filteredPaymentMethods?.length > 0
      ? filteredPaymentMethods[0]
      : roomData.bookingDetails && !approvedPaymentRequest
        ? paymentMethods.find((paymentMethod) =>
            roomData.guests.some((guest) => {
              if (isDemoTrip) return true
              return guest.userId === paymentMethod.userId
            }),
          ) || null
        : null

  useEffect(() => {
    if (initialPaymentMethod) {
      setFilteredPaymentMethods(filteredPaymentMethods)
      setSelectedPaymentMethod(initialPaymentMethod)
    }
  }, [initialPaymentMethod])

  const tripRoomGuests = prepareTripRoomGuestProfiles(roomData.guests, profiles, isLoadingProfiles)

  const selectedGuest = tripRoomGuests.find((guest) => guest.guestId === selectedGuestId)

  const { mutate: revealPassport, isPending: isPassportRevealing } = useMutation({
    mutationFn: async () => {
      // Should never happen, but extra fallback
      if (!selectedGuest.id || selectedGuest.id === -1) {
        alert(`${_t("errors.passportNumber")} ${_t("errors.tryAgainLater")}`)
        return
      }
      const passport = await revealPassportNumber({
        profileId: selectedGuest.id,
        tripId: activeTrip.id,
      })
      if (
        passport?.result?.passportNumber &&
        selectedGuest.passportTokenId === passport.result.passportTokenId
      ) {
        setUnmaskedPassportNumber(passport.result.passportNumber)
      }
    },
  })

  const gridData = selectedGuest
    ? isDemoTrip
      ? generateFakeProfileGridData({ firstName: selectedGuest?.firstName || "" })
      : generateProfileGridData({
          profile: isLoadingProfiles
            ? (tripRoomGuests?.[0] ?? generateSkeletonProfile())
            : selectedGuest,
          unmaskedPassportNumber,
          isPassportRevealing: isPassportRevealing,
        })
    : { personalInfo: [], residence: [], passportDetails: [], cruiseLineLoyaltyMembers: [] }

  return {
    tripRooms,
    roomData,
    tripRoomGuests,
    gridData,
    revealPassport,
  }
}
